import { FaGem, FaTicketAlt, FaUser } from "react-icons/fa";
import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import AnimatedCounter from "./AnimatedCounter";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import minerAnimation from "../animations/miner.json";
import { useUser } from "../context/UserContext";

const Header = ({ name, points, tickets, photo_url }) => {
  const { headerHidden } = useUser();
  const [isAnimating, setIsAnimating] = useState(true);
  const [noisePhase] = useState(Math.random());

  const [showFullBalance, setShowFullBalance] = useState(false);
  const balanceTimeoutRef = useRef(null);

  const handleBalanceClick = () => {
    setShowFullBalance(true);
    if (balanceTimeoutRef.current) clearTimeout(balanceTimeoutRef.current);
    balanceTimeoutRef.current = setTimeout(() => {
      setShowFullBalance(false);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (balanceTimeoutRef.current) clearTimeout(balanceTimeoutRef.current);
    };
  }, []);

  const formatBalance = (val) => {
    if (!showFullBalance && typeof val === 'number' && val >= 10000) {
      return (val / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return val;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: "-100%" }}
      animate={{ opacity: 1, y: headerHidden ? "-100%" : "0%" }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-0 w-full z-40"
      style={{
        WebkitBackdropFilter: "blur(12px)",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* SVG Liquid Glass Filter */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter
            id="liquid-glass-header"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feTurbulence
              type="fractalNoise"
              numOctaves="2"
              seed="3"
              result="turbulence"
              baseFrequency={`${0.008} ${0.008 + noisePhase * 0.003}`}
            />
            <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="2" />
            <feGaussianBlur stdDeviation="0.2" />
          </filter>
        </defs>
      </svg>

      <div
        className="relative flex justify-between items-center px-4 py-3"
        style={{
          filter: isAnimating ? "url(#liquid-glass-header)" : "none",
        }}
      >
        {/* Left: Avatar & Name */}
        <div className="flex items-center space-x-2 z-10">
          <motion.div
            whileHover={{ scale: 1.1, rotate: 360 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full shadow-md shrink-0"
          >
            {photo_url ? (
              <img
                src={photo_url}
                alt="User Avatar"
                className="w-7 h-auto rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                <FaUser className="text-white text-xl" />
              </div>
            )}
          </motion.div>
          <span className="font-semibold text-xs text-slate-900 dark:text-white drop-shadow truncate max-w-[80px]">
            {name}
          </span>
        </div>

        {/* Right: Gems & Tickets */}
        <div className="flex items-center space-x-1.5 z-10">
          <Link to="/miner" className="relative flex items-center justify-center w-9 h-9 mr-1 hover:scale-110 active:scale-95 transition-transform drop-shadow-md">
            <Lottie animationData={minerAnimation} loop={true} style={{ width: '100%', height: '100%' }} />
            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[7px] font-extrabold px-1 py-[1px] rounded-full shadow-sm animate-pulse leading-none">NEW</span>
          </Link>
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            className="yourGems flex items-center bg-gradient-to-r from-yellow-600 to-yellow-400 px-3 py-1 rounded-full shadow-md cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={handleBalanceClick}
          >
            <FaGem className="mr-2 text-yellow-100" />
            <span className="font-bold text-xs text-white"><AnimatedCounter value={formatBalance(points)} /></span>
          </motion.div>
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            className="yourTickets flex items-center bg-gradient-to-r from-red-500 to-red-700 px-3 py-1 rounded-full shadow-md"
            whileHover={{ scale: 1.05 }}
          >
            <FaTicketAlt className="mr-2 text-red-100" />
            <span className="font-bold text-xs text-white">
              <AnimatedCounter value={tickets} />
            </span>
          </motion.div>
        </div>

        {/* Glassy overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-white/15 to-white/10 rounded-b-2xl backdrop-blur-sm pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-purple-400/15 to-pink-400/10 rounded-b-2xl pointer-events-none" />
        <div className="absolute inset-0 rounded-b-2xl ring-1 ring-white/30 ring-inset pointer-events-none" />
        <div className="absolute inset-0 rounded-b-2xl bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
      </div>
    </motion.div>
  );
};

export default Header;
